// Sidekick usage page state <-> URL query string.
//
// Every control on the page lives in the query string so a view can be
// bookmarked or pasted to a colleague. Parsing is forgiving: unknown or
// malformed values fall back to the default rather than failing.

import {
  Asker,
  Client,
  ISidekickTurnsQuery,
  RatingFilter,
  TurnFlag,
} from "../../types/api/sidekick-usage.type";

export type RangePreset = "24h" | "7d" | "30d" | "custom";

export interface SidekickFilters {
  envIds: number[];
  range: RangePreset;
  // ISO-8601 strings; only used when range === "custom".
  from: string;
  to: string;
  users: string[];
  asker: Asker;
  rating: RatingFilter[];
  hasComment: "" | "true" | "false";
  flags: TurnFlag[];
  client: "" | Client;
  model: string;
  q: string;
  page: number;
  pageSize: number;
}

export const RANGE_PRESETS: { value: RangePreset; label: string; hours?: number }[] = [
  { value: "24h", label: "Last 24 hours", hours: 24 },
  { value: "7d", label: "Last 7 days", hours: 24 * 7 },
  { value: "30d", label: "Last 30 days", hours: 24 * 30 },
  { value: "custom", label: "Custom range" },
];

export const PAGE_SIZES = [25, 50, 100, 200];

const ASKERS: Asker[] = ["all", "customer", "staff"];
const RATINGS: RatingFilter[] = ["up", "down", "none"];
const FLAGS: TurnFlag[] = ["budget_exceeded", "answer_guard", "chart"];
const CLIENTS: Client[] = ["web", "ios"];

export const DEFAULT_FILTERS: SidekickFilters = {
  envIds: [],
  range: "7d",
  from: "",
  to: "",
  users: [],
  asker: "all",
  rating: [],
  hasComment: "",
  flags: [],
  client: "",
  model: "",
  q: "",
  page: 1,
  pageSize: 100,
};

const splitList = (value: string | null): string[] =>
  (value ?? "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const unique = <T,>(values: T[]): T[] => Array.from(new Set(values));

const pickList = <T extends string>(value: string | null, allowed: T[]): T[] =>
  unique(splitList(value).filter((v): v is T => (allowed as string[]).includes(v)));

const pickOne = <T extends string>(value: string | null, allowed: T[], fallback: T): T =>
  value !== null && (allowed as string[]).includes(value) ? (value as T) : fallback;

const positiveInt = (value: string | null, fallback: number): number => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const isoOrEmpty = (value: string | null): string => {
  if (!value) return "";
  const t = Date.parse(value);
  return Number.isNaN(t) ? "" : new Date(t).toISOString();
};

export const parseFilters = (params: URLSearchParams): SidekickFilters => {
  const envIds = unique(
    splitList(params.get("env_ids"))
      .map(Number)
      .filter((n) => Number.isInteger(n) && n > 0)
  );
  const pageSize = positiveInt(params.get("page_size"), DEFAULT_FILTERS.pageSize);
  return {
    envIds,
    range: pickOne(
      params.get("range"),
      RANGE_PRESETS.map((r) => r.value),
      DEFAULT_FILTERS.range
    ),
    from: isoOrEmpty(params.get("from")),
    to: isoOrEmpty(params.get("to")),
    users: unique(splitList(params.get("users"))),
    asker: pickOne(params.get("asker"), ASKERS, "all"),
    rating: pickList(params.get("rating"), RATINGS),
    hasComment: pickOne<SidekickFilters["hasComment"]>(
      params.get("has_comment"),
      ["true", "false"],
      ""
    ),
    flags: pickList(params.get("flags"), FLAGS),
    client: pickOne<SidekickFilters["client"]>(params.get("client"), CLIENTS, ""),
    model: params.get("model") ?? "",
    q: params.get("q") ?? "",
    page: positiveInt(params.get("page"), 1),
    pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : DEFAULT_FILTERS.pageSize,
  };
};

/** Inverse of parseFilters. Values equal to the default are left out. */
export const serializeFilters = (filters: SidekickFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.envIds.length) params.set("env_ids", filters.envIds.join(","));
  if (filters.range !== DEFAULT_FILTERS.range) params.set("range", filters.range);
  if (filters.range === "custom") {
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
  }
  if (filters.users.length) params.set("users", filters.users.join(","));
  if (filters.asker !== "all") params.set("asker", filters.asker);
  if (filters.rating.length) params.set("rating", filters.rating.join(","));
  if (filters.hasComment) params.set("has_comment", filters.hasComment);
  if (filters.flags.length) params.set("flags", filters.flags.join(","));
  if (filters.client) params.set("client", filters.client);
  if (filters.model) params.set("model", filters.model);
  if (filters.q) params.set("q", filters.q);
  if (filters.page !== 1) params.set("page", String(filters.page));
  if (filters.pageSize !== DEFAULT_FILTERS.pageSize)
    params.set("page_size", String(filters.pageSize));
  return params;
};

/**
 * Turn the range control into a concrete window. Presets end at "now" rounded
 * down to the minute, so repeated loads within a minute ask the same question
 * (and hit the agent's short-lived result cache).
 */
export const resolveWindow = (
  filters: SidekickFilters,
  now: Date = new Date()
): { from: string; to: string } => {
  const end = new Date(Math.floor(now.getTime() / 60000) * 60000);
  if (filters.range === "custom" && filters.from && filters.to) {
    return { from: filters.from, to: filters.to };
  }
  const preset =
    RANGE_PRESETS.find((r) => r.value === filters.range && r.hours) ??
    RANGE_PRESETS.find((r) => r.value === DEFAULT_FILTERS.range)!;
  const start = new Date(end.getTime() - (preset.hours as number) * 3600 * 1000);
  return { from: start.toISOString(), to: end.toISOString() };
};

export const toTurnsQuery = (
  filters: SidekickFilters,
  now: Date = new Date()
): ISidekickTurnsQuery => ({
  envIds: filters.envIds,
  ...resolveWindow(filters, now),
  users: filters.users,
  asker: filters.asker,
  rating: filters.rating,
  hasComment: filters.hasComment,
  flags: filters.flags,
  client: filters.client,
  model: filters.model,
  q: filters.q,
  page: filters.page,
  pageSize: filters.pageSize,
});
